import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import NiceModal from "@ebay/nice-modal-react";
import { MdRotateLeft } from "react-icons/md";
import { useFetch } from "../../../hooks/api";
import api from "../../../utils/api";
import { useDates } from "../../../dates/DatesContext";
import ProductCard from "../../../components/custom/merchandise/ProductCard";
import { ProductCell } from "../../merchandising/components/cells/ProductCell";
import BulkTagsForm from "../../../components/core/blocks/BulkTagsForm";
import { successToast, errorToast } from "../../../utils/toasts";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { EntityTagsFormModal } from "../../tags/components/EntityTagsFormModal";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { invalidateProductQueries } from "../../perf_reports/api/product/invalidateProductQueries";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../components/custom/merchandise/constants";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import { useClientTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn, customFilterFn } from "../../../components/table/filterFns";
import { idsToVisibilityState, resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

const columnHelper = createColumnBuilder();

const matchesInventoryState = (product, state) => {
  if (state === "IN_STOCK") {
    return product.inventory_state === state && product.stock_status === state;
  }
  return (
    product.inventory_state === state ||
    product.stock_status === state ||
    (product.stock_status === "IN_STOCK_SCARCE" && state === "LOW_STOCK")
  );
};

// Select filters hold `{ op, value: [...] }`, where "all" stands for no filter.
const inventoryStateFilterFn = (row, _columnId, filterValue) => {
  const states = [filterValue?.value ?? []].flat();
  return (
    states.length === 0 || states.includes("all") || states.some((state) => matchesInventoryState(row.original, state))
  );
};

const getSearchFields = (product, tagsEnhancedEnabled) =>
  [
    product.product_title,
    product.sku,
    product.asin,
    product.product_id,
    ...(tagsEnhancedEnabled ? (product.tags_enhanced ?? []).map((tag) => tag?.name) : (product.tags ?? [])),
  ]
    .filter((field) => field != null)
    .map(String);

export const CategoryProducts = ({ categoryId, marketPlace, channel, smallViewportWidthTable }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [filledTags, setFilledTags] = useState([]);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const queryClient = useQueryClient();
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const { group, start, end, preStart, preEnd } = useDates();

  const standardMetricIds = useStandardEntityMetrics("product");

  const filters = {
    mp: marketPlace.marketPlace,
    channel: channel,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "product_id",
    category: categoryId,
  };

  const productQuery = useFetch(
    ["product_report", marketPlace.marketPlace, start, end, preStart, preEnd, categoryId],
    "/api/data_report/product_report",
    filters,
    {
      select: (d) => d.data,
      enabled: !!categoryId,
      placeholderData: keepPreviousData,
    },
  );
  const products = productQuery.data?.products;

  const customTags = useMemo(() => {
    return [...new Set(products?.flatMap((product) => product.tags))];
  }, [products]);
  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(() => {
    if (tagsEnhancedEnabled) {
      return tagEnhancedOptions;
    }
    // Some Walmart products have their tags set to null instead of an empty array.
    const labelsByValue = new Map(customTags.filter(Boolean).map((tag) => [tag.toLowerCase(), tag]));
    return [
      { value: "all", label: ($) => $.advertising["All Tags"] },
      ...[...labelsByValue].map(([value, label]) => ({ value, label })),
    ];
  }, [customTags, tagEnhancedOptions, tagsEnhancedEnabled]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        id: "product_title",
        header: t(($) => $.advertising["Products"]),
        size: smallViewportWidthTable ? 250 : 350,
        minSize: 200,
        enableHiding: false,
        enableColumnFilter: false,
        sortFn: "alphanumeric",
        cell: ({ getValue, row }) => (
          <ProductCell
            value={getValue()}
            original={{ ...row.original, cellContext: "advertising" }}
            showInventoryBadge={true}
          />
        ),
      }),
      columnHelper.accessor((row) => row.stock_status ?? undefined, {
        id: "inventory_state",
        header: t(($) => $.advertising["Inventory State"]),
        enableHiding: false,
        enableSorting: false,
        filterFn: inventoryStateFilterFn,
        meta: { columnFilterOptions: { format: "select" } },
      }),
      columnHelper.accessor((row) => row.sales_state ?? undefined, {
        id: "sales_state",
        header: t(($) => $.advertising["Sales State"]),
        enableHiding: false,
        enableSorting: false,
        meta: { columnFilterOptions: { format: "select" } },
      }),
      columnHelper.accessor((row) => (row.active_ad_count ? "withActiveAds" : "withoutActiveAds"), {
        id: "active_ads",
        header: t(($) => $.advertising["Advertising Status"]),
        enableHiding: false,
        enableSorting: false,
        meta: { columnFilterOptions: { format: "single-select" } },
      }),
      columnHelper.accessor(
        (row) =>
          tagsEnhancedEnabled ?
            (row.tags_enhanced ?? []).map((tag) => tag.id)
          : (row.tags ?? []).filter(Boolean).map((tag) => tag.toLowerCase()),
        {
          id: "tags",
          header: t(($) => $.advertising["Custom Tags"]),
          enableHiding: false,
          enableSorting: false,
          meta: { columnFilterOptions: { format: "multi-select" } },
        },
      ),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [smallViewportWidthTable, standardMetricIds, tagsEnhancedEnabled, t],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "product_title",
    "price",
    "units",
    "sales",
    "total_acos",
    "page_views",
    "ad_sales",
    "ad_spend",
    "acos",
    "conversion_rate",
    "total_conv_rate",
    "orders",
    "ctr",
    "clicks",
    "impressions",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id),
  );

  const initialStateConfig = {
    columnVisibility: {
      // Hidden to support filtering
      inventory_state: false,
      sales_state: false,
      active_ads: false,
      tags: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["product_title"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const globalFilterFn = useMemo(
    () => commaSearchFilterFn((product) => getSearchFields(product, tagsEnhancedEnabled)),
    [tagsEnhancedEnabled],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "advertisingCategoryProducts",
    initialState: initialStateConfig,
  });

  const table = useClientTable({
    data: products,
    columns,
    getRowId: (row) => String(row.product_id),
    getRowCanExpand: () => true,
    enableRowSelection: userCanUpdateAds,
    defaultColumn: { filterFn: customFilterFn },
    globalFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: productQuery.isFetching && (!productQuery.data || productQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        inventory_state: [
          { value: "all", label: ($) => $.advertising["All Inventory States"] },
          ...INVENTORY_STATE_OPTIONS,
        ],
        sales_state: [{ value: "all", label: ($) => $.advertising["All Sales States"] }, ...SALE_STATE_OPTIONS],
        active_ads: [
          { value: "all", label: ($) => $.advertising["All Products"] },
          { value: "withActiveAds", label: ($) => $.advertising["Active Ads"] },
          { value: "withoutActiveAds", label: ($) => $.advertising["No Active Ads"] },
        ],
        tags: tagOptions,
      },
    },
  });

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const updateHandler = useMutation({
    mutationFn: async (body) => {
      return await api.put(`/api/gvads/productid/`, body);
    },

    onSuccess: () => {
      successToast({ message: t(($) => $.advertising["Product Ad Status Successfully Updated"]) });
      table.resetRowSelection();
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Product Ad Status"]), error });
      table.resetRowSelection();
      queryClient.invalidateQueries();
    },
  });

  // One request per product; the toasts report on all of them, not just the last to settle.
  const updateTags = useMutation({
    mutationFn: async (updates) => {
      const results = await Promise.allSettled(
        updates.map(({ product_id, tags }) => api.patch(`/api/gvads/productid/${product_id}`, { tags })),
      );
      const failure = results.find((result) => result.status === "rejected");
      if (failure) {
        throw failure.reason;
      }
    },

    onSuccess: () => {
      successToast({ message: t(($) => $.advertising["Product Tags Successfully Updated"]) });
      table.resetRowSelection();
      setFilledTags([]);
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Product Tags"]), error });
    },

    // Some products may have updated even when another request failed.
    onSettled: () => {
      invalidateProductQueries(queryClient);
    },
  });

  const updateProductAdStatus = (newStatus) => {
    updateHandler.mutate({
      product_ids: selected.map((id) => parseInt(id)),
      status: newStatus,
    });
  };

  const updateProductTags = (type = "add", tagIds) => {
    const selectedIds = new Set(selected);
    const updates = (products ?? [])
      .filter((product) => selectedIds.has(String(product.product_id)))
      .map((product) => {
        let updatedTags;
        if (tagsEnhancedEnabled) {
          const existingTagIds = product.tags_enhanced?.map((tag) => tag.id) ?? [];
          updatedTags =
            type === "remove" ? existingTagIds.filter((id) => !tagIds.includes(id)) : [...existingTagIds, ...tagIds];
        } else {
          updatedTags =
            type === "remove" ?
              (product.tags?.filter((tag) => !filledTags.includes(tag)) ?? [])
            : [...(product.tags ?? []), ...filledTags];
        }
        return { product_id: product.product_id, tags: [...new Set(updatedTags)] };
      });

    updateTags.mutate(updates);
  };

  const bulkActionOptions = [
    {
      label: t(($) => $.advertising["Pause Product Advertisements"], { count: selected.length }),
      action: () => {
        updateProductAdStatus("paused");
      },
      confirmationMessage: (
        <p>
          {t(
            ($) =>
              $.advertising["Are you sure you want to pause all product advertisements for the selected products?"],
            { count: selected.length },
          )}
        </p>
      ),
    },
    {
      label: t(($) => $.advertising["Enable Product Advertisements"], { count: selected.length }),
      action: () => {
        updateProductAdStatus("enabled");
      },
      confirmationMessage: (
        <p>
          {t(
            ($) =>
              $.advertising["Are you sure you want to enable all product advertisements for the selected products?"],
            { count: selected.length },
          )}
        </p>
      ),
    },
    ...(tagsEnhancedEnabled ?
      [
        {
          label: t(($) => $.advertising["Add Product Tags"]),
          action: () => {
            NiceModal.show(EntityTagsFormModal, {
              entityType: "PRODUCT",
              title: t(($) => $.advertising["Add Product Tags"]),
              onSubmit: (tagIds) => updateProductTags("add", tagIds),
            });
          },
        },
        {
          label: t(($) => $.advertising["Remove Product Tags"]),
          action: () => {
            NiceModal.show(EntityTagsFormModal, {
              entityType: "PRODUCT",
              title: t(($) => $.advertising["Remove Product Tags"]),
              onSubmit: (tagIds) => updateProductTags("remove", tagIds),
            });
          },
        },
      ]
    : [
        {
          label: t(($) => $.advertising["Add Product Tags"]),
          action: () => {
            updateProductTags();
          },
          actionFormFields: (
            <BulkTagsForm
              filledTags={filledTags}
              setFilledTags={setFilledTags}
              selected={selected}
              suggestedTags={customTags}
              taggedItemDescription={"product"}
              actionType={"add"}
            />
          ),
          confirmationMessage: (
            <p>
              {t(($) => $.advertising["Are you sure you want to add these tags from the selected products?"], {
                tagCount: filledTags.length,
                count: selected.length,
              })}
            </p>
          ),
        },
        {
          label: t(($) => $.advertising["Remove Product Tags"]),
          action: () => {
            updateProductTags("remove");
          },
          actionFormFields: (
            <BulkTagsForm
              filledTags={filledTags}
              setFilledTags={setFilledTags}
              selected={selected}
              suggestedTags={customTags}
              taggedItemDescription={"product"}
              actionType={"remove"}
            />
          ),
          confirmationMessage: (
            <p>
              {t(($) => $.advertising["Are you sure you want to remove these tags from the selected products?"], {
                tagCount: filledTags.length,
                count: selected.length,
              })}
            </p>
          ),
        },
      ]),
  ];

  return (
    <div>
      {userCanUpdateAds && (
        <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
          <BulkActionButton selected={selected} options={bulkActionOptions} />
        </Wrap>
      )}
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => (
          <ProductCard
            marketPlace={marketPlace}
            productId={row.original.product_id}
            channel={channel}
            initialTab="advertising"
            hideTabNav={true}
            hasBorder={false}
          />
        )}
      />
    </div>
  );
};
